import {
	AlertTriangle,
	Bot,
	ChevronDown,
	FileText,
	KeyRound,
	type LucideIcon,
	ShieldAlert,
	Users,
} from "lucide-react"
import { motion } from "framer-motion"
import { useState } from "react"
import { TRANSITION } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"
import type { AuditLogEntry, AuditPerson } from "../api/types"
import { AUDIT_REASON_LABELS, auditEventLabel, PLATFORM_LABELS } from "../lib/event-labels"
import { Reveal } from "@/shared/ui/reveal"

const SERVICE_ICON: Record<string, LucideIcon> = {
	auth: KeyRound,
	"catalog.post": FileText,
	"bot-builder": Bot,
	"catalog.sync": Users,
}

const time = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" })

const ROLE_LABEL: Record<string, string> = {
	ADMIN: "админ",
	MODERATOR: "модератор",
	USER: "пользователь",
}

function PersonChip({
	id,
	person,
	onPick,
}: {
	id: string
	person?: AuditPerson
	onPick: (id: string) => void
}) {
	return (
		<button
			type="button"
			onClick={(event) => {
				event.stopPropagation()
				onPick(id)
			}}
			title={
				person
					? [person.email, person.username && `@${person.username}`, ROLE_LABEL[person.role]]
							.filter(Boolean)
							.join(" · ")
					: id
			}
			className={cn(
				"font-medium text-foreground underline-offset-4 hover:underline",
				person?.deleted && "text-muted-foreground line-through",
			)}
		>
			{person?.name ?? `${id.slice(0, 8)}…`}
		</button>
	)
}

/** Подробности события: известные поля словами, остальное как есть. */
function Details({ entry }: { entry: AuditLogEntry }) {
	const rows: Array<[string, string]> = []
	const { reason, actorId: _actor, ...rest } = entry.metadata
	if (typeof reason === "string") rows.push(["Причина", AUDIT_REASON_LABELS[reason] ?? reason])
	for (const [key, value] of Object.entries(rest)) {
		rows.push([key, typeof value === "string" ? value : JSON.stringify(value)])
	}
	if (entry.request?.ip) rows.push(["IP", entry.request.ip])
	if (entry.request?.userAgent) rows.push(["Устройство", entry.request.userAgent])
	if (entry.request?.requestId) rows.push(["ID запроса", entry.request.requestId])
	rows.push(["Событие", entry.event])
	return (
		<dl className="grid gap-x-4 gap-y-1.5 rounded-xl bg-background/60 p-3 text-xs sm:grid-cols-[140px_minmax(0,1fr)]">
			{rows.map(([key, value]) => (
				<div key={key} className="contents">
					<dt className="text-muted-foreground">{key}</dt>
					<dd className="break-all">{value}</dd>
				</div>
			))}
		</dl>
	)
}

/** Запись журнала одной строкой: кто, что сделал, над кем и откуда. */
export function AuditEntryRow({
	entry,
	people,
	onPickUser,
	onPickActor,
}: {
	entry: AuditLogEntry
	people: Record<string, AuditPerson>
	onPickUser: (userId: string) => void
	onPickActor: (actorId: string) => void
}) {
	const [open, setOpen] = useState(false)
	const reason = typeof entry.metadata.reason === "string" ? entry.metadata.reason : null
	const actorId = typeof entry.metadata.actorId === "string" ? entry.metadata.actorId : null
	const Icon =
		entry.level === "error"
			? ShieldAlert
			: entry.level === "warn"
				? AlertTriangle
				: (SERVICE_ICON[entry.service] ?? FileText)
	const where = [
		entry.request
			? (PLATFORM_LABELS[entry.request.platform ?? "web"] ?? entry.request.platform)
			: null,
		entry.request?.ip,
	]
		.filter(Boolean)
		.join(" · ")

	// Проявление нужно записям, догруженным позже: первый показ ленты гасит AnimatePresence
	// списка через initial={false}.
	return (
		<motion.li
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={TRANSITION.base}
			className={cn(
				"rounded-xl transition-colors",
				open ? "bg-card" : "hover:bg-card/60",
				entry.level === "error" && "bg-red-500/5",
			)}
		>
			{/* Внутри строки есть кнопки с именами, поэтому сама строка не кнопка, а раскрываемая область. */}
			<div
				role="button"
				tabIndex={0}
				onClick={() => setOpen((value) => !value)}
				onKeyDown={(event) => {
					if (event.target !== event.currentTarget) return
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault()
						setOpen((value) => !value)
					}
				}}
				aria-expanded={open}
				className="flex w-full cursor-pointer items-start gap-3 rounded-xl px-3 py-2.5 text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
			>
				<span className="w-11 shrink-0 pt-0.5 text-xs text-muted-foreground tabular-nums">
					{time.format(new Date(entry.createdAt))}
				</span>
				<span
					className={cn(
						"grid size-7 shrink-0 place-items-center rounded-lg",
						entry.level === "error"
							? "bg-red-500/15 text-red-300"
							: entry.level === "warn"
								? "bg-amber-400/15 text-amber-300"
								: "bg-secondary text-muted-foreground",
					)}
				>
					<Icon className="size-3.5" />
				</span>
				<span className="min-w-0 flex-1 text-sm leading-snug">
					<span className="block">
						{actorId ? (
							<>
								<PersonChip id={actorId} person={people[actorId]} onPick={onPickActor} />
								<span className="text-muted-foreground"> · </span>
							</>
						) : null}
						<span>{auditEventLabel(entry.event)}</span>
						{entry.userId && entry.userId !== actorId ? (
							<>
								<span className="text-muted-foreground">{actorId ? " → " : ": "}</span>
								<PersonChip id={entry.userId} person={people[entry.userId]} onPick={onPickUser} />
							</>
						) : null}
						{reason ? (
							<span className="text-muted-foreground">
								, {AUDIT_REASON_LABELS[reason] ?? reason}
							</span>
						) : null}
					</span>
					{entry.message || where ? (
						<span className="mt-0.5 block truncate text-xs text-muted-foreground">
							{[entry.message, where].filter(Boolean).join(" · ")}
						</span>
					) : null}
				</span>
				<ChevronDown
					className={cn(
						"mt-1 size-4 shrink-0 text-muted-foreground transition-transform",
						open && "rotate-180",
					)}
				/>
			</div>
			{/* Подробности раскрываются по высоте: строки ниже отъезжают плавно, а не прыгают. */}
			<Reveal show={open} className="px-3 pb-3 sm:pl-[92px]">
				<Details entry={entry} />
			</Reveal>
		</motion.li>
	)
}
